import { gql } from '@apollo/client';
import {
  BUCKET_CHECK_SELECTION, CALCULATOR_LINK_SELECTION, COST_RATE_SELECTION, SCHEDULED_TASK_SELECTION, STORAGE_RECALC_SELECTION,
  TRASH_SETTINGS_SELECTION,
  SCENE_SETTINGS_SELECTION,
} from '../queries';

export const LOGIN_MUTATION = gql`
  mutation Login($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        firstName
        lastName
        email
        companyName
        industry
        role
        avatar
        permissions {
          sceneVisible
          matterportVisible
          releaseNotesVisible
          lgsxUpload
          video360Visible
          basemapVisible
          advancedTourVisible
        }
      }
    }
  }
`;

export const REGISTER_MUTATION = gql`
  mutation Register($input: RegisterInput!, $transferToken: String) {
    register(input: $input, transferToken: $transferToken) {
      token
      user {
        id
        firstName
        lastName
        email
        companyName
        role
        permissions {
          sceneVisible
          matterportVisible
          releaseNotesVisible
          lgsxUpload
          video360Visible
          basemapVisible
          advancedTourVisible
        }
      }
    }
  }
`;

export const CREATE_PROJECT_MUTATION = gql`
  mutation CreateProject($input: CreateProjectInput!) {
    createProject(input: $input) {
      id
      name
      description
      location
      latitude
      longitude
      epsg
      status
      storageUsed
      fileCount
      createdAt
      owner {
        id
        firstName
        lastName
      }
    }
  }
`;

export const UPDATE_PROJECT_MUTATION = gql`
  mutation UpdateProject($id: ID!, $input: UpdateProjectInput!) {
    updateProject(id: $id, input: $input) {
      id
      name
      description
      location
      latitude
      longitude
      epsg
      overviewPanel
      status
      updatedAt
    }
  }
`;

export const DELETE_PROJECT_MUTATION = gql`
  mutation DeleteProject($id: ID!) {
    deleteProject(id: $id) {
      success
      message
    }
  }
`;

export const SET_PROJECT_MATTERPORT_MUTATION = gql`
  mutation SetProjectMatterport($projectId: ID!, $url: String) {
    setProjectMatterport(projectId: $projectId, url: $url) {
      id
      matterportUrl
    }
  }
`;

export const REORDER_PROJECTS_MUTATION = gql`
  mutation ReorderProjects($projectIds: [ID!]!) {
    reorderProjects(projectIds: $projectIds)
  }
`;

export const DELETE_FILE_MUTATION = gql`
  mutation DeleteFile($id: ID!) {
    deleteFile(id: $id) {
      success
      message
    }
  }
`;

export const ADD_PROJECT_MEMBER_MUTATION = gql`
  mutation AddProjectMember($projectId: ID!, $userId: ID!, $role: MemberRole!) {
    addProjectMember(projectId: $projectId, userId: $userId, role: $role) {
      id
      members {
        user {
          id
          firstName
          lastName
          email
          avatar
        }
        role
        joinedAt
      }
    }
  }
`;

export const UPDATE_MEMBER_ROLE_MUTATION = gql`
  mutation UpdateMemberRole($projectId: ID!, $userId: ID!, $role: MemberRole!) {
    updateMemberRole(projectId: $projectId, userId: $userId, role: $role) {
      id
      members {
        user { id firstName lastName email }
        role
      }
    }
  }
`;

export const REMOVE_PROJECT_MEMBER_MUTATION = gql`
  mutation RemoveProjectMember($projectId: ID!, $userId: ID!) {
    removeProjectMember(projectId: $projectId, userId: $userId) {
      id
      members {
        user { id firstName lastName }
        role
      }
    }
  }
`;

export const CREATE_SHARED_LINK_MUTATION = gql`
  mutation CreateSharedLink($input: CreateSharedLinkInput!) {
    createSharedLink(input: $input) {
      id
      token
      permission
      expiresAt
      status
      viewCount
      maxViews
      recipientEmail
      note
      allowDownload
      allowScene
      allowPointCloudNotes
      allowGaussianNotes
      allowOrthoNotes
      allowPointCloudMeasurements
      allowOrthoMeasurements
      passwordProtected
      createdAt
    }
  }
`;

export const REVOKE_SHARED_LINK_MUTATION = gql`
  mutation RevokeSharedLink($id: ID!) {
    revokeSharedLink(id: $id) {
      id
      status
    }
  }
`;

export const UPDATE_PROFILE_MUTATION = gql`
  mutation UpdateProfile($input: UpdateProfileInput!) {
    updateProfile(input: $input) {
      id
      firstName
      lastName
      companyName
      industry
      avatar
      sendEmailsTo
    }
  }
`;

export const ADMIN_SET_USER_ROLE_MUTATION = gql`
  mutation AdminSetUserRole($userId: ID!, $role: UserRole!, $adminModules: [String!]) {
    adminSetUserRole(userId: $userId, role: $role, adminModules: $adminModules) {
      id
      role
      adminModules
    }
  }
`;

export const ADMIN_TOGGLE_USER_ACTIVE_MUTATION = gql`
  mutation AdminToggleUserActive($userId: ID!) {
    adminToggleUserActive(userId: $userId) {
      id
      isActive
    }
  }
`;

export const ADMIN_UPDATE_USER_PERMISSIONS_MUTATION = gql`
  mutation AdminUpdateUserPermissions($userId: ID!, $permissions: UserPermissionsInput!) {
    adminUpdateUserPermissions(userId: $userId, permissions: $permissions) {
      id
      permissions {
        sceneVisible
        lgsxUpload
        matterportVisible
        releaseNotesVisible
        gaussianVisible
        gaussianGenerate
        orthomosaicVisible
        video360Visible
        basemapVisible
        advancedTourVisible
      }
    }
  }
`;

export const ADMIN_RESET_USER_PASSWORD_MUTATION = gql`
  mutation AdminResetUserPassword($userId: ID!, $newPassword: String!) {
    adminResetUserPassword(userId: $userId, newPassword: $newPassword) {
      id
      firstName
      lastName
    }
  }
`;

export const ADMIN_RETRANSLATE_MUTATION = gql`
  mutation AdminRetranslate($fileId: ID!) {
    adminRetranslate(fileId: $fileId) {
      id
      apsStatus
      apsUrn
      errorMessage
      updatedAt
    }
  }
`;

export const ADMIN_SYNC_JOB_STATUS_MUTATION = gql`
  mutation AdminSyncJobStatus($fileId: ID!) {
    adminSyncJobStatus(fileId: $fileId) {
      id
      apsStatus
      apsUrn
      updatedAt
    }
  }
`;

export const ADMIN_RETRIGGER_CONVERSION_MUTATION = gql`
  mutation AdminRetriggerConversion($fileId: ID!) {
    adminRetriggerConversion(fileId: $fileId) {
      id
      status
      potreePath
      updatedAt
    }
  }
`;

export const ADMIN_OVERRIDE_JOB_STATUS_MUTATION = gql`
  mutation AdminOverrideJobStatus($fileId: ID!, $status: String!, $field: String!) {
    adminOverrideJobStatus(fileId: $fileId, status: $status, field: $field) {
      id
      status
      apsStatus
      updatedAt
    }
  }
`;

export const ADMIN_RESTORE_FILE_MUTATION = gql`
  mutation AdminRestoreFile($fileId: ID!) {
    adminRestoreFile(fileId: $fileId) {
      id
      originalName
      deletedAt
    }
  }
`;

export const ADMIN_PURGE_FILE_MUTATION = gql`
  mutation AdminPurgeFile($fileId: ID!) {
    adminPurgeFile(fileId: $fileId) {
      id
      purgedAt
      removing
      removesAt
    }
  }
`;

export const REQUEST_PASSWORD_RESET_MUTATION = gql`
  mutation RequestPasswordReset($email: String!) {
    requestPasswordReset(email: $email)
  }
`;

export const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($token: String!, $password: String!) {
    resetPassword(token: $token, password: $password)
  }
`;

export const DELETE_TRASH_ITEM_NOW_MUTATION = gql`
  mutation DeleteTrashItemNow($kind: String!, $id: ID!) {
    deleteTrashItemNow(kind: $kind, id: $id) {
      success
      message
    }
  }
`;

export const ADMIN_UPDATE_TRASH_SETTINGS_MUTATION = gql`
  mutation AdminUpdateTrashSettings($input: TrashSettingsInput!) {
    adminUpdateTrashSettings(input: $input) { ${TRASH_SETTINGS_SELECTION} }
  }
`;

export const ADMIN_UPDATE_FILE_FORMATS_MUTATION = gql`
  mutation AdminUpdateFileFormats($input: FileFormatsInput!) {
    adminUpdateFileFormats(input: $input) { documents { extension viewer } }
  }
`;

export const ADMIN_UPDATE_SCENE_SETTINGS_MUTATION = gql`
  mutation AdminUpdateSceneSettings($input: SceneSettingsInput!) {
    adminUpdateSceneSettings(input: $input) { ${SCENE_SETTINGS_SELECTION} }
  }
`;

export const ADMIN_UPDATE_SCHEDULED_TASK_MUTATION = gql`
  mutation AdminUpdateScheduledTask($key: String!, $enabled: Boolean!, $intervalSeconds: Int!) {
    adminUpdateScheduledTask(key: $key, enabled: $enabled, intervalSeconds: $intervalSeconds) { ${SCHEDULED_TASK_SELECTION} }
  }
`;

export const ADMIN_RUN_SCHEDULED_TASK_MUTATION = gql`
  mutation AdminRunScheduledTask($key: String!) {
    adminRunScheduledTask(key: $key) { ${SCHEDULED_TASK_SELECTION} }
  }
`;

export const ADMIN_DUPLICATE_FILE_MUTATION = gql`
  mutation AdminDuplicateFile($fileId: ID!, $targetProjectId: ID!) {
    adminDuplicateFile(fileId: $fileId, targetProjectId: $targetProjectId) {
      id
      originalName
      type
      status
      project { id name }
    }
  }
`;

const ANNOTATION_FIELDS = gql`
  fragment AnnotationFields on PointCloudAnnotation {
    id
    fileId
    createdBy { id firstName lastName }
    position { x y z }
    text
    status
    guestName
    replies {
      id
      createdBy { id firstName lastName }
      guestName
      text
      createdAt
    }
    attachments {
      id
      fileName
      mimeType
      fileSize
      createdAt
      url
      downloadUrl
      uploadedBy { id firstName lastName }
    }
    linkedFiles { id originalName type extension status }
    createdAt
    updatedAt
  }
`;

export const CREATE_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation CreateAnnotation($fileId: ID!, $position: AnnotationPositionInput!, $text: String!) {
    createAnnotation(fileId: $fileId, position: $position, text: $text) {
      ...AnnotationFields
    }
  }
`;

export const REPLY_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation ReplyToAnnotation($annotationId: ID!, $text: String!) {
    replyToAnnotation(annotationId: $annotationId, text: $text) {
      ...AnnotationFields
    }
  }
`;

export const RESOLVE_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation ResolveAnnotation($annotationId: ID!) {
    resolveAnnotation(annotationId: $annotationId) {
      ...AnnotationFields
    }
  }
`;

// Share-link guests (no account) — allowed per link by its creator.
export const SHARED_CREATE_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation SharedCreateAnnotation($token: String!, $fileId: ID!, $position: AnnotationPositionInput!, $text: String!, $guestName: String) {
    sharedCreateAnnotation(token: $token, fileId: $fileId, position: $position, text: $text, guestName: $guestName) {
      ...AnnotationFields
    }
  }
`;

export const SHARED_REPLY_ANNOTATION_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation SharedReplyToAnnotation($token: String!, $annotationId: ID!, $text: String!, $guestName: String) {
    sharedReplyToAnnotation(token: $token, annotationId: $annotationId, text: $text, guestName: $guestName) {
      ...AnnotationFields
    }
  }
`;

export const ADD_ANNOTATION_ATTACHMENT_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation AddAnnotationAttachment($annotationId: ID!, $s3Key: String!, $fileName: String!) {
    addAnnotationAttachment(annotationId: $annotationId, s3Key: $s3Key, fileName: $fileName) {
      ...AnnotationFields
    }
  }
`;

export const DELETE_ANNOTATION_ATTACHMENT_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation DeleteAnnotationAttachment($annotationId: ID!, $attachmentId: ID!) {
    deleteAnnotationAttachment(annotationId: $annotationId, attachmentId: $attachmentId) {
      ...AnnotationFields
    }
  }
`;

export const LINK_ANNOTATION_FILE_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation LinkAnnotationFile($annotationId: ID!, $fileId: ID!) {
    linkAnnotationFile(annotationId: $annotationId, fileId: $fileId) {
      ...AnnotationFields
    }
  }
`;

export const UNLINK_ANNOTATION_FILE_MUTATION = gql`
  ${ANNOTATION_FIELDS}
  mutation UnlinkAnnotationFile($annotationId: ID!, $fileId: ID!) {
    unlinkAnnotationFile(annotationId: $annotationId, fileId: $fileId) {
      ...AnnotationFields
    }
  }
`;

export const DELETE_ANNOTATION_MUTATION = gql`
  mutation DeleteAnnotation($annotationId: ID!) {
    deleteAnnotation(annotationId: $annotationId)
  }
`;

export const CREATE_MEASUREMENT_MUTATION = gql`
  mutation CreateMeasurement($fileId: ID!, $start: MeasurementPointInput!, $end: MeasurementPointInput!, $distance: Float!) {
    createMeasurement(fileId: $fileId, start: $start, end: $end, distance: $distance) {
      id
      kind
      createdBy { id firstName lastName }
      start { x y z }
      end { x y z }
      distance
      createdAt
    }
  }
`;

export const CREATE_AREA_MEASUREMENT_MUTATION = gql`
  mutation CreateAreaMeasurement($fileId: ID!, $points: [MeasurementPointInput!]!, $area: Float!) {
    createAreaMeasurement(fileId: $fileId, points: $points, area: $area) {
      id
      kind
      createdBy { id firstName lastName }
      points { x y z }
      area
      createdAt
    }
  }
`;

export const CREATE_2D_MEASUREMENT_MUTATION = gql`
  mutation Create2dMeasurement($fileId: ID!, $kind: String!, $points: [MeasurementPointInput!]!, $distance: Float, $area: Float, $name: String, $layout: String) {
    create2dMeasurement(fileId: $fileId, kind: $kind, points: $points, distance: $distance, area: $area, name: $name, layout: $layout) {
      id
      kind
      name
      layout
      points { x y z }
      distance
      area
      createdAt
    }
  }
`;

export const RENAME_MEASUREMENT_MUTATION = gql`
  mutation RenameMeasurement($measurementId: ID!, $name: String!) {
    renameMeasurement(measurementId: $measurementId, name: $name) {
      id
      name
    }
  }
`;

export const DELETE_MEASUREMENT_MUTATION = gql`
  mutation DeleteMeasurement($measurementId: ID!) {
    deleteMeasurement(measurementId: $measurementId)
  }
`;

// Share-link guests (no account) — allowed per link by its creator.
export const SHARED_CREATE_MEASUREMENT_MUTATION = gql`
  mutation SharedCreateMeasurement($token: String!, $fileId: ID!, $start: MeasurementPointInput!, $end: MeasurementPointInput!, $distance: Float!, $guestName: String) {
    sharedCreateMeasurement(token: $token, fileId: $fileId, start: $start, end: $end, distance: $distance, guestName: $guestName) {
      id
      kind
      createdBy { id firstName lastName }
      guestName
      fromThisLink
      start { x y z }
      end { x y z }
      distance
      createdAt
    }
  }
`;

export const SHARED_CREATE_AREA_MEASUREMENT_MUTATION = gql`
  mutation SharedCreateAreaMeasurement($token: String!, $fileId: ID!, $points: [MeasurementPointInput!]!, $area: Float!, $guestName: String) {
    sharedCreateAreaMeasurement(token: $token, fileId: $fileId, points: $points, area: $area, guestName: $guestName) {
      id
      kind
      createdBy { id firstName lastName }
      guestName
      fromThisLink
      points { x y z }
      area
      createdAt
    }
  }
`;

export const SHARED_DELETE_MEASUREMENT_MUTATION = gql`
  mutation SharedDeleteMeasurement($token: String!, $measurementId: ID!) {
    sharedDeleteMeasurement(token: $token, measurementId: $measurementId)
  }
`;

export const CREATE_FOCUS_ZONE_MUTATION = gql`
  mutation CreateFocusZone($fileId: ID!, $name: String!, $min: MeasurementPointInput!, $max: MeasurementPointInput!) {
    createFocusZone(fileId: $fileId, name: $name, min: $min, max: $max) {
      id
      name
      min { x y z }
      max { x y z }
      createdBy { id firstName lastName }
      createdAt
    }
  }
`;

export const RENAME_FOCUS_ZONE_MUTATION = gql`
  mutation RenameFocusZone($zoneId: ID!, $name: String!) {
    renameFocusZone(zoneId: $zoneId, name: $name) {
      id
      name
    }
  }
`;

export const DELETE_FOCUS_ZONE_MUTATION = gql`
  mutation DeleteFocusZone($zoneId: ID!) {
    deleteFocusZone(zoneId: $zoneId)
  }
`;

export const LOG_PROJECT_OPEN_MUTATION = gql`
  mutation LogProjectOpen($projectId: ID!) {
    logProjectOpen(projectId: $projectId)
  }
`;

export const CREATE_FILE_FOLDER_MUTATION = gql`
  mutation CreateFileFolder($projectId: ID!, $name: String!, $fileType: FileType!) {
    createFileFolder(projectId: $projectId, name: $name, fileType: $fileType) {
      id
      name
      fileType
      createdAt
    }
  }
`;

export const RENAME_FILE_FOLDER_MUTATION = gql`
  mutation RenameFileFolder($id: ID!, $name: String!) {
    renameFileFolder(id: $id, name: $name) {
      id
      name
    }
  }
`;

export const DELETE_FILE_FOLDER_MUTATION = gql`
  mutation DeleteFileFolder($id: ID!) {
    deleteFileFolder(id: $id) {
      success
    }
  }
`;

export const SET_FILE_FOLDER_MUTATION = gql`
  mutation SetFileFolder($fileId: ID!, $folderId: ID) {
    setFileFolder(fileId: $fileId, folderId: $folderId) {
      id
      folderId
    }
  }
`;

export const ADMIN_CREATE_USER_PLAN_MUTATION = gql`
  mutation AdminCreateUserPlan($name: String!, $accountStorage: Float!, $filesDownloadPerMonth: Int!, $downloadGbPerMonth: Float, $schedulingPriority: Int, $gaussianPerMonth: Int, $limitedTime: Boolean, $limitedTimeDays: Int, $allowSelectForUpgrade: Boolean, $maxSubusers: Int) {
    adminCreateUserPlan(name: $name, accountStorage: $accountStorage, filesDownloadPerMonth: $filesDownloadPerMonth, downloadGbPerMonth: $downloadGbPerMonth, schedulingPriority: $schedulingPriority, gaussianPerMonth: $gaussianPerMonth, limitedTime: $limitedTime, limitedTimeDays: $limitedTimeDays, allowSelectForUpgrade: $allowSelectForUpgrade, maxSubusers: $maxSubusers) {
      id
      name
      accountStorage
      filesDownloadPerMonth
      downloadGbPerMonth
      schedulingPriority
      gaussianPerMonth
      limitedTime
      limitedTimeDays
      allowSelectForUpgrade
      maxSubusers
      userCount
    }
  }
`;

export const ADMIN_UPDATE_USER_PLAN_MUTATION = gql`
  mutation AdminUpdateUserPlan($id: ID!, $name: String!, $accountStorage: Float!, $filesDownloadPerMonth: Int!, $downloadGbPerMonth: Float, $schedulingPriority: Int, $gaussianPerMonth: Int, $limitedTime: Boolean, $limitedTimeDays: Int, $allowSelectForUpgrade: Boolean, $maxSubusers: Int) {
    adminUpdateUserPlan(id: $id, name: $name, accountStorage: $accountStorage, filesDownloadPerMonth: $filesDownloadPerMonth, downloadGbPerMonth: $downloadGbPerMonth, schedulingPriority: $schedulingPriority, gaussianPerMonth: $gaussianPerMonth, limitedTime: $limitedTime, limitedTimeDays: $limitedTimeDays, allowSelectForUpgrade: $allowSelectForUpgrade, maxSubusers: $maxSubusers) {
      id
      name
      accountStorage
      filesDownloadPerMonth
      downloadGbPerMonth
      schedulingPriority
      gaussianPerMonth
      limitedTime
      limitedTimeDays
      allowSelectForUpgrade
      maxSubusers
      userCount
    }
  }
`;

export const ADMIN_DELETE_USER_PLAN_MUTATION = gql`
  mutation AdminDeleteUserPlan($id: ID!) {
    adminDeleteUserPlan(id: $id) {
      success
      message
    }
  }
`;

export const ADMIN_ASSIGN_USER_PLAN_MUTATION = gql`
  mutation AdminAssignUserPlan($userId: ID!, $planId: ID) {
    adminAssignUserPlan(userId: $userId, planId: $planId) {
      id
      userPlan {
        id
        name
      }
    }
  }
`;

export const ADMIN_UPDATE_PLATFORM_SETTINGS_MUTATION = gql`
  mutation AdminUpdatePlatformSettings($usersAllowedToRegister: Boolean!, $defaultUserPlanId: ID, $newUserPermissions: UserPermissionsInput) {
    adminUpdatePlatformSettings(usersAllowedToRegister: $usersAllowedToRegister, defaultUserPlanId: $defaultUserPlanId, newUserPermissions: $newUserPermissions) {
      usersAllowedToRegister
      defaultUserPlan {
        id
        name
      }
      newUserPermissions { sceneVisible lgsxUpload matterportVisible releaseNotesVisible gaussianVisible gaussianGenerate orthomosaicVisible video360Visible basemapVisible advancedTourVisible }
    }
  }
`;

// Soft delete / restore, admin-only. A deleted project disappears for its owner
// and members but stays in the admin list, marked.
export const ADMIN_DELETE_PROJECT_MUTATION = gql`
  mutation AdminDeleteProject($projectId: ID!) {
    adminDeleteProject(projectId: $projectId) {
      id
      deletedAt
    }
  }
`;

export const ADMIN_RESTORE_PROJECT_MUTATION = gql`
  mutation AdminRestoreProject($projectId: ID!) {
    adminRestoreProject(projectId: $projectId) {
      id
      deletedAt
    }
  }
`;

// Remove a member from any project (the owner-facing mutation is owner-only).
export const ADMIN_REMOVE_PROJECT_MEMBER_MUTATION = gql`
  mutation AdminRemoveProjectMember($projectId: ID!, $userId: ID!) {
    adminRemoveProjectMember(projectId: $projectId, userId: $userId) {
      projectId
      name
      owner {
        id
        firstName
        lastName
        email
      }
      members {
        role
        joinedAt
        user {
          id
          firstName
          lastName
          email
          companyName
        }
      }
    }
  }
`;

// Direct admin ownership change. Returns the project so the row's owner
// updates in place; the previous owner stays on as an editor.
export const ADMIN_CHANGE_PROJECT_OWNER_MUTATION = gql`
  mutation AdminChangeProjectOwner(
    $projectId: ID!
    $newOwnerId: ID!
    $ignoreStorageLimit: Boolean
    $keepPreviousOwner: Boolean
  ) {
    adminChangeProjectOwner(
      projectId: $projectId
      newOwnerId: $newOwnerId
      ignoreStorageLimit: $ignoreStorageLimit
      keepPreviousOwner: $keepPreviousOwner
    ) {
      id
      owner {
        id
        firstName
        lastName
        email
      }
    }
  }
`;

export const ADMIN_UPDATE_PROJECT_OPEN_LOG_LIMIT_MUTATION = gql`
  mutation AdminUpdateProjectOpenLogLimit($projectId: ID!, $openLogLimit: Int!) {
    adminUpdateProjectOpenLogLimit(projectId: $projectId, openLogLimit: $openLogLimit) {
      id
      openLogLimit
      openLogs {
        email
        ip
        at
      }
    }
  }
`;


export const CREATE_RELEASE_NOTE_MUTATION = gql`
  mutation CreateReleaseNote($version: String!, $items: [ReleaseNoteItemInput!]!, $releaseDate: String) {
    createReleaseNote(version: $version, items: $items, releaseDate: $releaseDate) {
      id
      version
    }
  }
`;

export const UPDATE_RELEASE_NOTE_MUTATION = gql`
  mutation UpdateReleaseNote($id: ID!, $version: String!, $items: [ReleaseNoteItemInput!]!, $releaseDate: String) {
    updateReleaseNote(id: $id, version: $version, items: $items, releaseDate: $releaseDate) {
      id
      version
    }
  }
`;

export const DELETE_RELEASE_NOTE_MUTATION = gql`
  mutation DeleteReleaseNote($id: ID!) {
    deleteReleaseNote(id: $id)
  }
`;

export const GENERATE_RELEASE_NOTE_SHARE_LINK_MUTATION = gql`
  mutation GenerateReleaseNoteShareLink($id: ID!) {
    generateReleaseNoteShareLink(id: $id) {
      id
      shareToken
    }
  }
`;

export const REVOKE_RELEASE_NOTE_SHARE_LINK_MUTATION = gql`
  mutation RevokeReleaseNoteShareLink($id: ID!) {
    revokeReleaseNoteShareLink(id: $id) {
      id
      shareToken
    }
  }
`;

export const GENERATE_GAUSSIAN_SPLAT_MUTATION = gql`
  mutation GenerateGaussianSplat($fileId: ID!) {
    generateGaussianSplat(fileId: $fileId) {
      id
      status
      type
    }
  }
`;

export const ADMIN_UPDATE_COST_RATES_MUTATION = gql`
  mutation AdminUpdateCostRates($input: CostRatesInput!) {
    adminUpdateCostRates(input: $input) { ${COST_RATE_SELECTION} }
  }
`;

export const ADMIN_START_BUCKET_CHECK_MUTATION = gql`
  mutation AdminStartBucketCheck {
    adminStartBucketCheck {${BUCKET_CHECK_SELECTION}}
  }
`;

// Folders of the last check's orphan list: the given paths, or every deletable one.
export const ADMIN_DELETE_BUCKET_ORPHANS_MUTATION = gql`
  mutation AdminDeleteBucketOrphans($paths: [String!], $all: Boolean) {
    adminDeleteBucketOrphans(paths: $paths, all: $all) {${BUCKET_CHECK_SELECTION}}
  }
`;

// Without a userId: every project owner, one after another.
export const ADMIN_RECALCULATE_STORAGE_MUTATION = gql`
  mutation AdminRecalculateStorage($userId: ID) {
    adminRecalculateStorage(userId: $userId) {${STORAGE_RECALC_SELECTION}}
  }
`;

export const ADMIN_CREATE_CALCULATOR_SHARE_LINK_MUTATION = gql`
  mutation AdminCreateCalculatorShareLink($label: String, $expiresAt: String) {
    adminCreateCalculatorShareLink(label: $label, expiresAt: $expiresAt) { ${CALCULATOR_LINK_SELECTION} }
  }
`;

export const ADMIN_REVOKE_CALCULATOR_SHARE_LINK_MUTATION = gql`
  mutation AdminRevokeCalculatorShareLink($id: ID!) {
    adminRevokeCalculatorShareLink(id: $id) { ${CALCULATOR_LINK_SELECTION} }
  }
`;

export const ADMIN_UPDATE_SAFETY_LIMITS_MUTATION = gql`
  mutation AdminUpdateSafetyLimits($input: SafetyLimitsInput!) {
    adminUpdateSafetyLimits(input: $input) {
      maxFilesPointCloud
      maxFilesGaussian
      maxFiles2d
      maxFiles3d
      maxFilesDocument
      maxFilesOrthomosaic
      maxFilesVideo360
      maxSizePointCloudMb
      maxSizeGaussianMb
      maxSize2dMb
      maxSize3dMb
      maxSizeDocumentMb
      maxSizeOrthomosaicMb
      maxSizeVideo360Mb
      maxNotesPointCloud
      maxNotesGaussian
      maxAttachmentsPerNote
      maxAttachmentSizeMb
      maxGuestNotesPerHour
      maxSharedLinksPerProject
    }
  }
`;

export const ADMIN_UPDATE_BASEMAP_SETTINGS_MUTATION = gql`
  mutation AdminUpdateBasemapSettings($input: BasemapSettingsInput!) {
    adminUpdateBasemapSettings(input: $input) {
      esriApiKey
      googleApiKey
      terrainEnabled
      googleStatus
    }
  }
`;

export const SET_PROJECT_BASEMAP_MUTATION = gql`
  mutation SetProjectBasemap($projectId: ID!, $basemap: ProjectBasemapInput!) {
    setProjectBasemap(projectId: $projectId, basemap: $basemap) {
      id
      basemap { provider opacity terrain height position gridAdjust { dx dy rotation pivotX pivotY } }
    }
  }
`;

export const SET_PROJECT_SITE_PLACEMENT_MUTATION = gql`
  mutation SetProjectSitePlacement($projectId: ID!, $placement: SitePlacementInput) {
    setProjectSitePlacement(projectId: $projectId, placement: $placement) {
      id
      sitePlacement { lat lng heading originX originY originZ }
    }
  }
`;

// An empty name gives the Virtual Tour area its automatic name back.
export const SET_TOUR_AREA_NAME_MUTATION = gql`
  mutation SetTourAreaName($projectId: ID!, $stationId: String!, $name: String, $mode: TourAreaMode) {
    setTourAreaName(projectId: $projectId, stationId: $stationId, name: $name, mode: $mode) {
      id
      tourAreas { mode names { stationId name mode } groups { id name stationIds } }
    }
  }
`;

export const SET_TOUR_AREA_MODE_MUTATION = gql`
  mutation SetTourAreaMode($projectId: ID!, $mode: TourAreaMode!) {
    setTourAreaMode(projectId: $projectId, mode: $mode) {
      id
      tourAreas { mode names { stationId name mode } groups { id name stationIds } }
    }
  }
`;

export const SAVE_TOUR_AREA_GROUPS_MUTATION = gql`
  mutation SaveTourAreaGroups($projectId: ID!, $groups: [TourAreaGroupInput!]!) {
    saveTourAreaGroups(projectId: $projectId, groups: $groups) {
      id
      tourAreas { mode names { stationId name mode } groups { id name stationIds } }
    }
  }
`;

export const ADMIN_UPDATE_DELIVERY_SETTINGS_MUTATION = gql`
  mutation AdminUpdateDeliverySettings($input: DeliverySettingsInput!) {
    adminUpdateDeliverySettings(input: $input) {
      intelligentTiering
      intelligentTieringSince
      downloadsViaCloudfront
      cloudfrontConfigured
      maxCdnDownloadGb
    }
  }
`;

export const ADMIN_UPDATE_ARCHIVE_SETTINGS_MUTATION = gql`
  mutation AdminUpdateArchiveSettings($input: ArchiveSettingsInput!) {
    adminUpdateArchiveSettings(input: $input) {
      enabled
      delayedDownloadDays
      fullArchiveDays
      restoreKeepDays
    }
  }
`;

export const ADMIN_UPDATE_EMAIL_SETTINGS_MUTATION = gql`
  mutation AdminUpdateEmailSettings($input: EmailSettingsInput!) {
    adminUpdateEmailSettings(input: $input) {
      enabled
      provider
      fromName
      fromAddress
      appUrl
      sesRegion
      smtpHost
      smtpPort
      smtpSecure
      smtpUsername
      smtpPasswordSet
    }
  }
`;

export const ADMIN_SEND_TEST_EMAIL_MUTATION = gql`
  mutation AdminSendTestEmail($to: String!) {
    adminSendTestEmail(to: $to) {
      ok
      error
    }
  }
`;

export const REQUEST_PROJECT_TRANSFER_MUTATION = gql`
  mutation RequestProjectTransfer($projectId: ID!, $email: String!) {
    requestProjectTransfer(projectId: $projectId, email: $email) {
      id
      toEmail
      status
      createdAt
    }
  }
`;

export const CANCEL_PROJECT_TRANSFER_MUTATION = gql`
  mutation CancelProjectTransfer($projectId: ID!) {
    cancelProjectTransfer(projectId: $projectId)
  }
`;

export const ACCEPT_PROJECT_TRANSFER_MUTATION = gql`
  mutation AcceptProjectTransfer($token: String!) {
    acceptProjectTransfer(token: $token) {
      id
      name
    }
  }
`;

export const SET_FILE_SCENE_TRANSFORM_MUTATION = gql`
  mutation SetFileSceneTransform($fileId: ID!, $transform: SceneTransformInput) {
    setFileSceneTransform(fileId: $fileId, transform: $transform) {
      id
      sceneTransform { x y z rot scale }
    }
  }
`;

export const INVITE_SUBUSER_MUTATION = gql`
  mutation InviteSubuser($email: String!, $firstName: String!, $lastName: String!) {
    inviteSubuser(email: $email, firstName: $firstName, lastName: $lastName) { id email status }
  }
`;

export const RESEND_SUBUSER_INVITE_MUTATION = gql`
  mutation ResendSubuserInvite($userId: ID!) {
    resendSubuserInvite(userId: $userId) { id status }
  }
`;

export const SET_SUBUSER_ACTIVE_MUTATION = gql`
  mutation SetSubuserActive($userId: ID!, $active: Boolean!) {
    setSubuserActive(userId: $userId, active: $active) { id status }
  }
`;

export const REMOVE_SUBUSER_MUTATION = gql`
  mutation RemoveSubuser($userId: ID!) {
    removeSubuser(userId: $userId) { success message }
  }
`;

export const SET_SUBUSER_PROJECT_ROLE_MUTATION = gql`
  mutation SetSubuserProjectRole($userId: ID!, $projectId: ID!, $role: MemberRole) {
    setSubuserProjectRole(userId: $userId, projectId: $projectId, role: $role) { projectId name role }
  }
`;

export const ACCEPT_SUBUSER_INVITE_MUTATION = gql`
  mutation AcceptSubuserInvite($token: String!, $password: String!) {
    acceptSubuserInvite(token: $token, password: $password) {
      token
      user { id firstName lastName email companyName role isSubuser }
    }
  }
`;
